import React, { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { User, KeyRound, Save, CheckCircle, AlertCircle } from "lucide-react";

function Card({ title, children, testid }) {
  return (
    <div className="rounded-lg border border-zinc-800 bg-[#0b0b0f] p-5" data-testid={testid}>
      <h3 className="text-sm font-semibold text-zinc-100 mb-4">{title}</h3>
      {children}
    </div>
  );
}

export default function Account() {
  const { user, refresh } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const saveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      const body = {};
      if (name !== user?.name) body.name = name;
      if (newPassword) {
        body.current_password = currentPassword;
        body.new_password = newPassword;
      }
      if (Object.keys(body).length === 0) {
        setMessage({ type: "info", text: "Nothing to change." });
        setSaving(false);
        return;
      }
      await api.patch("/auth/me", body);
      await refresh();
      setMessage({ type: "success", text: "Profile updated." });
      setCurrentPassword("");
      setNewPassword("");
    } catch (e) {
      setMessage({ type: "error", text: formatApiError(e.response?.data?.detail) || e.message });
    }
    setSaving(false);
  };

  return (
    <div className="max-w-lg mx-auto space-y-6">
      <div>
        <h1 className="text-xl font-bold text-zinc-100">Account</h1>
        <p className="text-sm text-zinc-500 mt-1">Manage your name, password, and plan.</p>
      </div>

      {message && (
        <div className={`flex items-center gap-2 text-sm px-4 py-3 rounded-md border ${
          message.type === "success"
            ? "border-emerald-800 bg-emerald-950/40 text-emerald-300"
            : message.type === "error"
            ? "border-red-800 bg-red-950/40 text-red-300"
            : "border-zinc-700 bg-zinc-900 text-zinc-300"
        }`}>
          {message.type === "success" ? <CheckCircle size={15} /> : <AlertCircle size={15} />}
          {message.text}
        </div>
      )}

      <form onSubmit={saveProfile} className="space-y-6">
        <Card title="Profile" testid="account-profile">
          <div className="space-y-4">
            <div>
              <label className="block text-xs text-zinc-500 mb-1">Email</label>
              <input
                type="email"
                value={user?.email || ""}
                disabled
                className="w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-500 cursor-not-allowed"
              />
              <p className="text-[11px] text-zinc-600 mt-1">Email cannot be changed.</p>
            </div>
            <div>
              <label className="block text-xs text-zinc-500 mb-1">Display name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100 focus:outline-none focus:ring-1 focus:ring-zinc-600"
                placeholder="Your name"
                maxLength={100}
              />
            </div>
          </div>
        </Card>

        <Card title="Change password" testid="account-password">
          <div className="space-y-4">
            <div>
              <label className="block text-xs text-zinc-500 mb-1">Current password</label>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100 focus:outline-none focus:ring-1 focus:ring-zinc-600"
                placeholder="Leave blank to keep current"
                autoComplete="current-password"
              />
            </div>
            <div>
              <label className="block text-xs text-zinc-500 mb-1">New password</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100 focus:outline-none focus:ring-1 focus:ring-zinc-600"
                placeholder="Min 8 characters"
                minLength={8}
                autoComplete="new-password"
              />
            </div>
          </div>
        </Card>

        <Card title="Plan" testid="account-plan">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-sm text-zinc-100 capitalize">{user?.plan || "free"}</span>
              <p className="text-xs text-zinc-500 mt-0.5">
                {user?.subscription_status
                  ? `Subscription: ${user.subscription_status}`
                  : "Free plan — no active subscription"}
              </p>
            </div>
            <span className={`text-xs px-2 py-0.5 rounded-full ${
              user?.plan === "power" ? "bg-amber-950/50 text-amber-300 border border-amber-800" :
              user?.plan === "pro" ? "bg-blue-950/50 text-blue-300 border border-blue-800" :
              "bg-zinc-800 text-zinc-400"
            }`}>
              {user?.plan || "free"}
            </span>
          </div>
        </Card>

        <button
          type="submit"
          disabled={saving}
          data-testid="btn-save-account"
          className="w-full flex items-center justify-center gap-2 rounded-md bg-zinc-100 text-zinc-950 px-4 py-2.5 text-sm font-medium hover:bg-zinc-200 disabled:opacity-50 transition-colors"
        >
          <Save size={15} />
          {saving ? "Saving…" : "Save changes"}
        </button>
      </form>
    </div>
  );
}
